import { AlertTriangle } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { avatarColorIndex, nameInitials } from '@/components/ui/avatar';
import { cn } from '@/components/ui/cn';
import { ExportMenu } from '@/components/ui/ExportMenu';
import { StatePanel } from '@/components/ui/StatePanel';
import { useBackState } from '@/lib/back-nav';

import {
  dateRangeError,
  eventBadge,
  originLabel,
  printLogErrorCopy,
  purposeLabel,
  versionLabel,
  printLogExportQuery,
} from '../presentation';
import { usePrintLog } from '../use-watermark';
import { usePrintLogUrlState } from '../use-url-state';

import { PrintLogFilters } from './PrintLogFilters';
import { formatDateTime } from '@/lib/time';
import { skeletonLine } from '@/components/ui/skeleton';

const COLUMNS = [
  'Evento',
  'Documento',
  'Usuario',
  'Versión',
  'Copia',
  'Origen',
  'Fecha y hora',
  'Folio',
];

/** Bitácora de impresión y descarga de toda la empresa (so=400), con filtros en la URL. */
export function PrintLogCard({ companyId }: { companyId: string }): ReactElement {
  const { filters, active, setFilters, clear } = usePrintLogUrlState();
  const fromHere = useBackState('Marca de agua e impresión');
  // Un rango inválido (enlace editado a mano o fecha tecleada) se explica sin consultar.
  const rangeError = dateRangeError(filters.desde, filters.hasta);
  const query = usePrintLog(filters, { companyId, enabled: rangeError === null });
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  const docCode =
    filters.doc === null
      ? null
      : (items.find((i) => i.entryId === filters.doc)?.documentCode ?? null);
  const personName =
    filters.persona === null
      ? null
      : (items.find((i) => i.userId === filters.persona)?.userDisplayName ?? null);

  return (
    <div className="card rise d3" style={{ marginTop: 16 }}>
      <div className="ch">
        <h3 id="print-log-title">Registro de impresión y descarga</h3>
        <span className="count">eventos en el sistema</span>
        {/* Exportación (spec 2026-10-14 §3): lo filtrado completo, en CSV o PDF. */}
        {rangeError === null && (
          <ExportMenu
            compact
            path="/api/v1/quality/print-events/export"
            query={printLogExportQuery(filters)}
            title="Registro de impresión y descarga"
          />
        )}
      </div>
      <PrintLogFilters
        companyId={companyId}
        filters={filters}
        active={active}
        docCode={docCode}
        personName={personName}
        onChange={setFilters}
        onClear={clear}
      />
      {rangeError !== null ? (
        <p className="cb fld-err" role="alert">
          {rangeError}
        </p>
      ) : query.isLoading ? (
        <div className="cb" aria-busy="true" aria-label="Cargando registro">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className={skeletonLine({ width: 90, space: 'y' })} />
          ))}
        </div>
      ) : query.isError ? (
        <div className="cb">
          <StatePanel
            icon={<AlertTriangle />}
            title="No se pudo cargar el registro"
            tone="danger"
            role="alert"
            onRetry={() => void query.refetch()}
            retrying={query.isRefetching}
          >
            {printLogErrorCopy(query.error)}
          </StatePanel>
        </div>
      ) : items.length === 0 ? (
        <p className="cb soft" role="status">
          {active ? 'Sin eventos con estos filtros.' : 'Todavía no hay copias registradas.'}
        </p>
      ) : (
        <>
          <div className="t-scroll">
            <table className="t" aria-labelledby="print-log-title">
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((e) => {
                  const badge = eventBadge(e.eventType);
                  const name = e.userDisplayName ?? 'Usuario sin nombre';
                  return (
                    <tr key={e.id}>
                      <td>
                        <span className={cn('badge', badge.cls)}>
                          <span className="dotb" aria-hidden="true" />
                          {badge.label}
                        </span>
                      </td>
                      <td>
                        <Link to={`/documentos/${e.entryId}`} state={fromHere} className="code">
                          {e.documentCode}
                        </Link>
                      </td>
                      <td>
                        <div className="who">
                          <span
                            className={cn('av sm', `p${avatarColorIndex(e.userId)}`)}
                            aria-hidden="true"
                          >
                            {nameInitials(name)}
                          </span>
                          <div>
                            <span className="nm">{name}</span>
                            {e.currentName !== null && (
                              <div className="rl">Hoy: {e.currentName}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="dim-mono">
                        {versionLabel(e.documentVersion, e.revisionLabel)}
                      </td>
                      <td className="dim-mono">{purposeLabel(e.purpose)}</td>
                      <td className="dim-mono">{originLabel(e.ip)}</td>
                      <td className="dim-mono">{formatDateTime(e.occurredAt)}</td>
                      <td className="dim-mono">{e.folio}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {query.hasNextPage && (
            <div className="cb nmore">
              <button
                type="button"
                className="btn mut sm"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {query.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
